import { computePosition, flip, offset, arrow, autoUpdate, hide, shift, size } from '@floating-ui/dom';
const TOP_START = 'top-start';
const TOP = 'top';
const TOP_END = 'top-end';
const RIGHT_START = 'right-start';
const RIGHT = 'right';
const RIGHT_END = 'right-end';
const BOTTOM_START = 'bottom-start';
const BOTTOM = 'bottom';
const BOTTOM_END = 'bottom-end';
const LEFT_START = 'left-start';
const LEFT = 'left';
const LEFT_END = 'left-end';
export const directions = [
    TOP_START,
    TOP,
    TOP_END,
    RIGHT_START,
    RIGHT,
    RIGHT_END,
    BOTTOM_START,
    BOTTOM,
    BOTTOM_END,
    LEFT_START,
    LEFT,
    LEFT_END,
];
export const opposites = {
    [TOP_START]: BOTTOM_START,
    [TOP]: BOTTOM,
    [TOP_END]: BOTTOM_END,
    [BOTTOM_START]: TOP_START,
    [BOTTOM]: TOP,
    [BOTTOM_END]: TOP_END,
    [LEFT_START]: RIGHT_START,
    [LEFT]: RIGHT,
    [LEFT_END]: RIGHT_END,
    [RIGHT_START]: LEFT_START,
    [RIGHT]: LEFT,
    [RIGHT_END]: LEFT_END,
};
const rotation = {
    [LEFT_START]: -45,
    [LEFT]: -45,
    [LEFT_END]: -45,
    [TOP_START]: 45,
    [TOP]: 45,
    [TOP_END]: 45,
    [RIGHT_START]: 135,
    [RIGHT]: 135,
    [RIGHT_END]: 135,
    [BOTTOM_START]: -135,
    [BOTTOM]: -135,
    [BOTTOM_END]: -135,
};
export const arrowDirectionClassname = (dir) => {
    let direction;
    if (/-/.test(dir)) {
        direction = dir
            .split('-')
            .map((d) => d.charAt(0).toUpperCase() + d.slice(1))
            .join('');
    }
    else {
        direction = (dir.charAt(0).toUpperCase() + dir.slice(1));
    }
    return direction;
};
const side = (dir) => dir.split('-')[0];
const staticSide = (dir) => opposites[side(dir)];
const arrowDirection = (dir) => opposites[dir];
const arrowRotation = (dir) => rotation[arrowDirection(dir)];
const applyArrowStyles = (arrowEl, arrowRotation, dir) => {
    Object.assign(arrowEl?.style, {
        borderTopLeftRadius: '4px',
        zIndex: 1,
        // border alignment is off by a fraction of a pixel, this fixes it
        [`margin${arrowDirectionClassname(staticSide(dir))}`]: '-0.5px',
        transform: `rotate(${arrowRotation}deg)`,
    });
};
const ELEMENT_PADDING = 8;
const ARROW_OFFSET = 24;
export async function useRecompute(state) {
    if (!state?.isShowing)
        return; // we're not currently showing the element, no reason to recompute
    if (state?.waitForDOM) {
        await state?.waitForDOM(); // wait for DOM to settle before computing
    }
    if (!state?.targetEl || !state?.attentionEl)
        return;
    let targetEl = state?.targetEl;
    const attentionEl = state.attentionEl;
    computePosition(targetEl, attentionEl, {
        placement: state?.directionName ?? BOTTOM,
        middleware: [
            offset({ mainAxis: state?.distance ?? 8, crossAxis: state?.skidding ?? 0 }), // offers flexibility over how to place the attentionEl towards its targetEl both on the x and y axis (horizontally and vertically).
            state?.flip &&
                flip({
                    // when flip is set to true it will move the attentionEl's placement to its opposite side or to the preferred placements if fallbackPlacements has a value
                    crossAxis: state?.crossAxis, // checks overflow to trigger a flip. When disabled, it will ignore overflow
                    fallbackPlacements: state?.fallbackPlacements,
                }),
            state?.flip && shift({ crossAxis: true }), // shifts the attentionEl to make sure that it stays in view
            !state?.noArrow && state?.arrowEl && arrow({ element: state?.arrowEl }),
            hide(), // will hide the attentionEl when it appears detached from the targetEl. Can be called multiple times in the middleware-array if you want to use several strategies. Default strategy is 'referenceHidden'.
            size({
                apply() {
                    // Apply equal padding to the left and right sides of the attentionEl to prevent it from overflowing the viewport on smaller screens.
                    Object.assign(attentionEl.style, {
                        paddingRight: `${ELEMENT_PADDING}px`,
                        paddingLeft: `${ELEMENT_PADDING}px`,
                    });
                },
            }),
        ],
    }).then(({ x, y, middlewareData, placement }) => {
        state.actualDirection = placement;
        // We only want floating-ui to calculate the callout's arrow position and not the attention element's position, since the targetEl can be undefined if isCallout is true.
        // Therefore, we only add this style to the attentionEl when isCallout is false.
        if (!state?.isCallout) {
            Object.assign(attentionEl.style, {
                left: `${x}px`,
                top: `${y}px`,
            });
        }
        // Handle visibility based on hide middleware
        if (middlewareData?.hide && !state?.isCallout) {
            const { referenceHidden } = middlewareData.hide;
            Object.assign(attentionEl.style, {
                visibility: referenceHidden ? 'hidden' : '',
            });
        }
        // Arrow position adjustment
        if (middlewareData?.arrow && state?.arrowEl) {
            const arrowEl = state?.arrowEl;
            const { x: arrowX, y: arrowY } = middlewareData.arrow;
            const isRtl = window.getComputedStyle(attentionEl).direction === 'rtl'; // Checks RTL for proper arrow alignment
            const arrowPlacement = arrowDirection(placement).split('-')[1];
            let top = '', right = '', bottom = '', left = '';
            // Adjust based on 'start' or 'end' placements
            if (arrowPlacement === 'start') {
                const value = typeof arrowX === 'number' ? `calc(${ARROW_OFFSET}px - ${arrowEl.offsetWidth / 2}px)` : '';
                top = typeof arrowY === 'number' ? `calc(${ARROW_OFFSET}px - ${arrowEl.offsetWidth / 2}px)` : '';
                right = isRtl ? value : '';
                left = isRtl ? '' : value;
            }
            else if (arrowPlacement === 'end') {
                const value = typeof arrowX === 'number' ? `calc(${ARROW_OFFSET}px - ${arrowEl.offsetWidth / 2}px)` : '';
                right = isRtl ? '' : value;
                left = isRtl ? value : '';
                bottom = typeof arrowY === 'number' ? `calc(${ARROW_OFFSET}px - ${arrowEl.offsetWidth / 2}px)` : '';
            }
            else {
                // Default positioning with no 'start' or 'end'
                left = typeof arrowX === 'number' ? `${arrowX}px` : '';
                top = typeof arrowY === 'number' ? `${arrowY}px` : '';
            }
            // Apply the arrow styles
            Object.assign(arrowEl.style, {
                top,
                right,
                bottom,
                left,
            });
            // Apply arrow rotation styles
            applyArrowStyles(arrowEl, arrowRotation(placement), placement);
        }
    });
    return state;
}
export const autoUpdatePosition = (state) => {
    // computePosition is only run once, so we need to wrap autoUpdate() around useRecompute() in order to recompute the attentionEl's position
    // autoUpdate adds event listeners that are triggered on resize and on scroll and will keep calling the useRecompute().
    // autoUpdate returns a cleanup() function that removes the event listeners.
    if (!state?.targetEl || !state?.attentionEl || state?.isCallout)
        return;
    return autoUpdate(state?.targetEl, state?.attentionEl, () => {
        useRecompute(state);
    });
};
